import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const headerRef = useRef(null);
  const storyRef = useRef(null);
  const valuesRef = useRef(null);
  const approachRef = useRef(null);

  useEffect(() => {
    // Animation sobre d'entrée de la page
    const tl = gsap.timeline();
    
    tl.fromTo(headerRef.current.querySelector('h1'),
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" }
    )
    .fromTo(headerRef.current.querySelector('.lead'),
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" },
      "-=0.4"
    );

    // Animation sobre de la section histoire
    gsap.fromTo(storyRef.current.querySelectorAll('.about-content > div'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: storyRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation sobre des valeurs
    gsap.fromTo(valuesRef.current.querySelectorAll('.value-card'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: valuesRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation sobre de l'approche
    gsap.fromTo(approachRef.current.querySelectorAll('.approach-item'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: approachRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const philosophy = [
    { title: 'Respect de l\'animal', description: 'Toujours travailler AVEC le chien, jamais contre lui' },
    { title: 'Observation & écoute', description: 'Comprendre les émotions et besoins avant d\'agir' },
    { title: 'Coopération', description: 'Favoriser la motivation et la confiance mutuelle' },
    { title: 'Pédagogie & transmission', description: 'Vous rendre autonome pas à pas' },
    { title: 'Approche personnalisée', description: 'Aucune recette unique, chaque duo est unique' },
    { title: 'Bienveillance', description: 'Patience, clarté et honnêteté, sans jugement' }
  ];

  const approachSteps = [
    { step: '1', title: 'Accueil & écoute', description: 'Comprendre votre quotidien et vos objectifs' },
    { step: '2', title: 'Évaluation & plan', description: 'Identifier les besoins et définir un programme sur-mesure' },
    { step: '3', title: 'Mise en pratique', description: 'Séances progressives basées sur la coopération' },
    { step: '4', title: 'Suivi', description: 'Ajustements et accompagnement continu' }
  ];

  return (
    <div className="about-page">
      {/* Header */}
      <section ref={headerRef} className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>À Propos d'Atout Canin</h1>
            <p className="lead">
              Découvrez ma passion pour l'éducation canine<br />
              et mon approche respectueuse du bien-être animal
            </p>
          </div>
        </div>
      </section>

      {/* Qui suis-je */}
      <section ref={storyRef} className="section">
        <div className="container">
          <div className="about-content">
            <div>
              <h2>Qui suis-je ?</h2>
              <p className="lead">Bonjour et bienvenue,</p>
              <p>
                Je m’appelle Clara, je suis éducatrice comportementaliste canine diplômée d’État, spécialisée en éducation positive et bienveillante. Mon objectif est simple : vous aider à construire une relation sereine, complice et équilibrée avec votre chien, dans le respect de ses émotions, de son rythme et de vos besoins.
              </p>
              <h3 className="mt-md">Une vocation née d’une rencontre… avec June</h3>
              <p>
                Tout a commencé avec June, ma chienne au tempérament aussi sensible qu’attachant. Avec elle, rien n’a été simple au départ. Elle m’a obligée à ralentir, à observer, à remettre en question les méthodes toutes faites. Elle m’a appris que derrière chaque comportement difficile se cache souvent une émotion non entendue, un besoin non comblé.
              </p>
              <p>
                June a été ma meilleure professeure. Grâce à elle, j’ai découvert la puissance de la communication canine, de la confiance mutuelle, et surtout : l’importance de travailler AVEC le chien, jamais CONTRE lui. C’est elle qui m’a menée sur le chemin de ce métier et la preuve qu’avec écoute, patience et bienveillance, tout est possible.
              </p>
            </div>
            <div className="about-image">
              <img src="/hero/June3.jpg" alt="Clara et June" />
            </div>
          </div>
        </div>
      </section>

      {/* Mon parcours & philosophie */}
      <section ref={valuesRef} className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Mon parcours et mes engagements</h2>
            <p className="lead">Formée au Lycée agricole de Cibeins, avec une formation complète et professionnalisante d’éducateur canin.</p>
          </div>
          <ul className="grid grid-3" style={{ listStyle: 'none', padding: 0 }}>
            <li className="card"><h3>Éducation & rééducation</h3><p>Approche basée sur la motivation et la coopération</p></li>
            <li className="card"><h3>Éthologie & psychologie</h3><p>Comprendre l\'animal pour mieux l\'accompagner</p></li>
            <li className="card"><h3>Relation humain-chien</h3><p>Prise en compte de votre réalité et de votre rythme</p></li>
          </ul>
          <div className="section-header" style={{ marginTop: 'var(--spacing-xl)' }}>
            <h2>Ma philosophie</h2>
            <p className="lead">Travailler avec respect, écoute et coopération</p>
          </div>
          <div className="grid grid-3">
            {philosophy.map((v, i) => (
              <div key={i} className="card value-card text-center">
                <h3>{v.title}</h3>
                <p>{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mon approche */}
      <section ref={approachRef} className="section">
        <div className="container">
          <div className="section-header">
            <h2>Mon engagement envers vous</h2>
            <p className="lead">Vous guider pas à pas, sans jugement, pour un quotidien plus harmonieux</p>
          </div>
          
          <div className="grid grid-2">
            {approachSteps.map((step, index) => (
              <div key={index} className="approach-item card">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-md)' }}>
                  <div 
                    style={{
                      width: '50px',
                      height: '50px',
                      background: 'var(--gradient-warm)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--white)',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    {step.step}
                  </div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container">
          <div className="text-center">
            <h2>Envie d\'en discuter ?</h2>
            <p className="lead">Contactez-moi pour un bilan personnalisé et un accompagnement sur-mesure</p>
            <Link to="/services" className="btn btn-primary btn-large" style={{ marginRight: 'var(--spacing-sm)' }}>
              Découvrir mes services
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-large">
              Me contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Conditions de Participation */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Conditions de Participation</h2>
            <p className="lead">
              Pour garantir la sécurité et le bien-être de tous les participants
            </p>
          </div>
          
          <div className="grid grid-2">
            <div className="condition-item card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <div className="condition-icon">🆔</div>
                <div>
                  <h4>Identification</h4>
                  <p>Chien identifié par puce électronique ou tatouage</p>
                </div>
              </div>
            </div>
            
            <div className="condition-item card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <div className="condition-icon">💉</div>
                <div>
                  <h4>Vaccinations</h4>
                  <p>Vaccinations à jour selon le protocole vétérinaire</p>
                </div>
              </div>
            </div>
            
            <div className="condition-item card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <div className="condition-icon">🦠</div>
                <div>
                  <h4>Antiparasitaires</h4>
                  <p>Traitement antiparasitaire récent et efficace</p>
                </div>
              </div>
            </div>
            
            <div className="condition-item card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <div className="condition-icon">🎂</div>
                <div>
                  <h4>Âge minimum</h4>
                  <p>Chien âgé d'au moins 3 mois</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="text-center">
            <h2>Prêt à faire confiance à Atout Canin ?</h2>
            <p className="lead">
              Contactez-nous pour découvrir comment nous pouvons aider votre compagnon à s'épanouir
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/services" className="btn btn-primary btn-large">
                Découvrir nos services
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-large">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Atout Canin - Éducation et Rééducation Canine. Tous droits réservés.</p>
          <p>
            <Link to="/cgv">Conditions Générales de Vente</Link>
            <Link to="/contact">Mentions Légales</Link>
          </p>
        </div>
      </footer>


    </div>
  );
};

export default About; 